import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, MapPin } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import {
  PHONE_TEL,
  PHONE_DISPLAY,
  ADDRESS,
  INSTAGRAM_URL,
  FACEBOOK_URL,
  GOOGLE_BUSINESS_URL,
  DIAMOND_URL,
} from "@/site";

export default function Footer() {
  const { t } = useLang();
  const f = t.footer;

  return (
    <footer className="relative border-t border-gold/15 bg-coal" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-10 sm:py-20">
        <div className="flex flex-col items-center text-center">
          <Link to="/" data-testid="footer-logo-link" aria-label="Gunè — Home">
            <img src="/img/logo.png" alt="Gunè" className="h-20 w-auto" />
          </Link>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-neutral-400">
            {f.tagline}
          </p>
          <div className="mt-8 flex items-center gap-4">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              aria-label="Instagram"
              className="flex h-11 w-11 items-center justify-center border border-gold/25 text-gold transition-all duration-500 hover:bg-gold hover:text-black"
            >
              <Instagram size={17} />
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              aria-label="Facebook"
              className="flex h-11 w-11 items-center justify-center border border-gold/25 text-gold transition-all duration-500 hover:bg-gold hover:text-black"
            >
              <Facebook size={17} />
            </a>
            <a
              href={GOOGLE_BUSINESS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-google-link"
              aria-label="Google Business"
              className="flex h-11 w-11 items-center justify-center border border-gold/25 text-gold transition-all duration-500 hover:bg-gold hover:text-black"
            >
              <MapPin size={17} />
            </a>
          </div>
        </div>

        <div className="mt-16 grid gap-10 border-t border-gold/10 pt-12 text-center sm:grid-cols-3 sm:text-left">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              {f.explore}
            </p>
            <div className="mt-4 flex flex-col gap-2.5">
              <Link to="/" data-testid="footer-nav-home" className="text-sm text-neutral-300 transition-colors hover:text-gold">{t.nav.home}</Link>
              <Link to="/chi-siamo" data-testid="footer-nav-about" className="text-sm text-neutral-300 transition-colors hover:text-gold">{t.nav.about}</Link>
              <Link to="/menu" data-testid="footer-nav-menu" className="text-sm text-neutral-300 transition-colors hover:text-gold">{t.nav.menu}</Link>
              <Link to="/contatti" data-testid="footer-nav-contact" className="text-sm text-neutral-300 transition-colors hover:text-gold">{t.nav.contact}</Link>
            </div>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              {f.contacts}
            </p>
            <div className="mt-4 flex flex-col gap-2.5 text-sm text-neutral-300">
              <span>{ADDRESS}</span>
              <a
                href={PHONE_TEL}
                data-testid="footer-phone-link"
                className="inline-flex items-center gap-2 text-neutral-300 transition-colors hover:text-gold"
              >
                <Phone size={13} className="text-gold" />
                {PHONE_DISPLAY}
              </a>
            </div>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              {f.hoursT}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-neutral-300">{f.hoursV}</p>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center gap-3 border-t border-gold/10 pt-8 text-center sm:flex-row sm:justify-between sm:text-left">
          <p className="font-display italic text-lg text-white/70">Gunè</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            © {new Date().getFullYear()} Ristorante Gunè — {f.rights}
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            {f.credit}{" "}
            <a
              href={DIAMOND_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-diamond-credit"
              className="text-gold transition-colors duration-300 hover:text-gold-light"
            >
              Diamond Innovations
            </a>
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          <Link
            to="/privacy-policy"
            data-testid="footer-legal-privacy"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 transition-colors duration-300 hover:text-gold"
          >
            {f.legal.privacy}
          </Link>
          <Link
            to="/termini-e-condizioni"
            data-testid="footer-legal-terms"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 transition-colors duration-300 hover:text-gold"
          >
            {f.legal.terms}
          </Link>
          <Link
            to="/cookie-policy"
            data-testid="footer-legal-cookies"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 transition-colors duration-300 hover:text-gold"
          >
            {f.legal.cookies}
          </Link>
        </div>
      </div>
      {/* spacer for the mobile floating call bar */}
      <div className="h-14 lg:hidden" aria-hidden="true" />
    </footer>
  );
}
